import React, { useState, useEffect } from 'react';
import { ArrowRight, Clock, Leaf, Droplets, Flame, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useBooking } from '../contexts/BookingContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_23900c27-228f-4c3d-aeba-b97a1fc3d686/artifacts/36ug4hft_fd4bbdec7dd10598cfe459ce916091f1.jpg";
const PRICE_LIST_IMAGE = "https://customer-assets.emergentagent.com/job_7f45736e-df57-4742-9820-4e4efb4e31c0/artifacts/edh0203k_IMG-20260130-WA0018.jpg";

const SpaWellnessPage = () => {
  const { language, t } = useLanguage();
  const { openBooking } = useBooking();
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMassage, setSelectedMassage] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const getDescription = (service) => {
    const descKey = `description_${language}`;
    return service[descKey] || service.description_en || '';
  };

  const getName = (service) => {
    const nameKey = `name_${language}`;
    return service[nameKey] || service.name_en || service.name_pt || '';
  };

  const getCategoryLabel = (cat) => {
    const labelKey = `label_${language}`;
    return cat[labelKey] || cat.label_en || '';
  };

  const spaCategories = [
    { key: 'massage', label_pt: 'Massagens', label_en: 'Massages', label_fr: 'Massages', label_de: 'Massagen', label_es: 'Masajes', icon: Flame },
    { key: 'body_care', label_pt: 'Tratamentos Corporais', label_en: 'Body Care', label_fr: 'Soins Corporels', label_de: 'Körperpflege', label_es: 'Cuidado Corporal', icon: Droplets },
    { key: 'spa_formulas', label_pt: 'Fórmulas Wellness', label_en: 'Wellness Formulas', label_fr: 'Formules Wellness', label_de: 'Wellness-Formeln', label_es: 'Fórmulas Wellness', icon: Leaf },
  ];

  return (
    <div className="min-h-screen bg-kamit-beige pt-20" data-testid="spa-wellness-page">
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE}
            alt="Spa & Wellness"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-kamit-dark/70 via-kamit-dark/50 to-kamit-dark/80" />
        </div>
        <div className="relative z-10 text-center px-4">
          <p 
            className="text-kamit-gold text-2xl md:text-3xl mb-4"
            style={{ fontFamily: "'Tangerine', cursive", fontWeight: 700 }}
          >
            Kamit's Hands On Therapy
          </p>
          <h1 className="font-heading text-5xl md:text-7xl text-kamit-beige mb-4">
            Spa & Wellness
          </h1>
          <p className="text-kamit-beige/80 text-lg max-w-2xl mx-auto mb-8">
            {language === 'pt'
              ? 'Massagens terapêuticas e tratamentos corporais para o seu bem-estar'
              : language === 'fr'
              ? 'Massages thérapeutiques et soins corporels pour votre bien-être'
              : language === 'de'
              ? 'Therapeutische Massagen und Körperbehandlungen für Ihr Wohlbefinden'
              : language === 'es'
              ? 'Masajes terapéuticos y tratamientos corporales para su bienestar'
              : 'Therapeutic massages and body treatments for your well-being'
            }
          </p>
          <Button 
            onClick={() => openBooking('massage')}
            className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full px-8 py-6 text-lg btn-shine"
            data-testid="spa-hero-book-btn"
          >
            {t('nav_book')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 bg-kamit-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl text-kamit-brown mb-6">
            {language === 'pt' ? 'A Nossa Filosofia' 
             : language === 'fr' ? 'Notre Philosophie'
             : language === 'de' ? 'Unsere Philosophie'
             : language === 'es' ? 'Nuestra Filosofía'
             : 'Our Philosophy'}
          </h2>
          <p className="text-kamit-brown/70 leading-relaxed text-lg">
            {language === 'pt'
              ? 'Utilizamos fitoterapia, naturopatia e o conhecimento holístico ancestral da Mãe África, assim como os 5 elementos, para que a sua sessão seja uma experiência humanoterapêutica cheia de generosidade e, acima de tudo, de cura.'
              : language === 'fr'
              ? 'Nous utilisons la phytothérapie, la naturopathie et le savoir holistique ancestral de Mère Afrique, ainsi que les 5 éléments, pour que votre séance soit une expérience humanothérapeutique pleine de générosité et, surtout, de guérison.'
              : language === 'de'
              ? 'Wir verwenden Phytotherapie, Naturheilkunde und das uralte ganzheitliche Wissen von Mutter Afrika sowie die 5 Elemente, damit Ihre Sitzung eine humanotherapeutische Erfahrung voller Großzügigkeit und vor allem Heilung ist.'
              : language === 'es'
              ? 'Utilizamos fitoterapia, naturopatía y el conocimiento holístico ancestral de la Madre África, así como los 5 elementos, para que su sesión sea una experiencia humanoterapéutica llena de generosidad y, sobre todo, de curación.'
              : 'We use phytotherapy, naturopathy and the ancestral holistic knowledge of Mother Africa, as well as the 5 elements, so that your session is a humanotherapeutic experience full of generosity and, above all, healing.'
            }
          </p>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl text-kamit-brown mb-4">
              {language === 'pt' ? 'Os Nossos Tratamentos' 
               : language === 'fr' ? 'Nos Traitements'
               : language === 'de' ? 'Unsere Behandlungen'
               : language === 'es' ? 'Nuestros Tratamientos'
               : 'Our Treatments'}
            </h2>
            <div className="w-24 h-1 bg-kamit-gold mx-auto" />
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-kamit-gold border-t-transparent rounded-full mx-auto" />
            </div>
          ) : (
            <Tabs defaultValue="massage">
              <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent mb-12">
                {spaCategories.map((cat) => {
                  const IconComponent = cat.icon;
                  return (
                    <TabsTrigger
                      key={cat.key}
                      value={cat.key}
                      className="px-6 py-3 rounded-full data-[state=active]:bg-kamit-brown data-[state=active]:text-kamit-beige text-kamit-brown border border-kamit-brown/20 flex items-center gap-2"
                      data-testid={`tab-${cat.key}`}
                    >
                      <IconComponent className="h-4 w-4" />
                      {getCategoryLabel(cat)}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {spaCategories.map((cat) => (
                <TabsContent key={cat.key} value={cat.key}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {services[cat.key]?.map((service) => (
                      <div
                        key={service.id}
                        className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all border border-kamit-brown/5 group ${service.description_en ? 'cursor-pointer' : ''}`}
                        data-testid={`service-${service.id}`}
                        onClick={() => service.description_en && setSelectedMassage(service)}
                      >
                        <h3 className="font-heading text-xl text-kamit-brown mb-2 group-hover:text-kamit-gold transition-colors">
                          {getName(service)}
                        </h3>
                        {service.description_en && (
                          <p className="text-kamit-brown/60 text-sm mb-3 line-clamp-2">
                            {getDescription(service).substring(0, 100)}...
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-kamit-gold font-semibold text-lg">
                            {service.price}
                          </span>
                          {service.duration && (
                            <span className="flex items-center gap-1 text-kamit-brown/60 text-sm">
                              <Clock className="h-4 w-4" />
                              {service.duration}
                            </span>
                          )}
                        </div>
                        {service.description_en && (
                          <p className="text-kamit-gold text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            {language === 'fr' ? 'Cliquez pour voir les détails →' 
                             : language === 'pt' ? 'Clique para ver detalhes →' 
                             : language === 'de' ? 'Klicken Sie für Details →'
                             : language === 'es' ? 'Haz clic para ver detalles →'
                             : 'Click to view details →'}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          )}

          {/* Massage Detail Modal */}
          {selectedMassage && (
            <div 
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedMassage(null)}
            >
              <div 
                className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-kamit-brown p-6 rounded-t-2xl">
                  <button 
                    onClick={() => setSelectedMassage(null)}
                    className="absolute top-4 right-4 text-kamit-beige/80 hover:text-kamit-beige transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                  <h3 className="font-heading text-2xl text-kamit-beige pr-8">
                    {getName(selectedMassage)}
                  </h3>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-kamit-gold font-semibold text-xl">
                      {selectedMassage.price}
                    </span>
                    {selectedMassage.duration && (
                      <span className="flex items-center gap-1 text-kamit-beige/70 text-sm">
                        <Clock className="h-4 w-4" />
                        {selectedMassage.duration}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-kamit-brown/80 leading-relaxed text-lg">
                    {getDescription(selectedMassage)}
                  </p>
                  <div className="mt-8 flex gap-4">
                    <Button 
                      onClick={() => {
                        setSelectedMassage(null);
                        openBooking('massage');
                      }}
                      className="flex-1 bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full py-6 text-lg"
                      data-testid="spa-service-book-btn"
                    >
                      {language === 'fr' ? 'Réserver Maintenant' 
                       : language === 'pt' ? 'Reservar Agora' 
                       : language === 'de' ? 'Jetzt Buchen'
                       : language === 'es' ? 'Reservar Ahora'
                       : 'Book Now'}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Menu / Price List Section */}
      <section className="py-16 bg-kamit-brown/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl text-kamit-brown mb-4">
              {language === 'fr' ? 'Menu Kamit' : language === 'pt' ? 'Menu Kamit' : 'Kamit Menu'}
            </h2>
            <div className="w-24 h-1 bg-kamit-gold mx-auto" />
          </div>
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <img 
              src={PRICE_LIST_IMAGE}
              alt="Kamit Spa Menu"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-kamit-brown">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl text-kamit-beige mb-4">
            {language === 'pt' ? 'Descubra o Relaxamento Total' 
             : language === 'fr' ? 'Découvrez la Relaxation Totale'
             : language === 'de' ? 'Entdecken Sie Totale Entspannung'
             : language === 'es' ? 'Descubre la Relajación Total'
             : 'Discover Total Relaxation'}
          </h2>
          <p className="text-kamit-beige/70 mb-8 text-lg">
            {language === 'pt'
              ? 'Reserve o seu momento de bem-estar hoje.'
              : language === 'fr'
              ? 'Réservez votre moment de bien-être aujourd\'hui.'
              : language === 'de'
              ? 'Buchen Sie noch heute Ihren Moment des Wohlbefindens.'
              : language === 'es'
              ? 'Reserve su momento de bienestar hoy.'
              : 'Book your moment of wellness today.'
            }
          </p>
          <Button 
            onClick={() => openBooking('massage')}
            className="bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full px-10 py-6 text-lg btn-shine"
            data-testid="spa-cta-book-btn"
          >
            {t('nav_book')}
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default SpaWellnessPage;
